import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors } from 'state';
import { BlueprintVariant } from '../../state/app/blueprints/types';
import {
  loadPending,
  savePending,
  TandemError,
  TandemPair,
  tandemRequest,
} from './api';

type Target = {
  id: number;
  name: string;
  trainer_id: number;
  trainer_username: string;
};
type Input = {
  key: string;
  blueprint_id: number;
  variant_id: number;
  trainer_id: number;
  klass_id: number;
};
export default function TandemLaunch({
  blueprintId,
  variant,
  published,
}: {
  blueprintId: number;
  variant: BlueprintVariant;
  published: boolean;
}) {
  const username = useSelector(selectors.username);
  const storage = `tandem-create:${username}:${variant.id}`;
  const [pending, setPending] = useState<Input | null>(() =>
    loadPending(storage),
  );
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<Target[]>([]);
  const [trainer, setTrainer] = useState<number | undefined>(
    pending?.trainer_id,
  );
  const [klass, setKlass] = useState<number | undefined>(pending?.klass_id);
  const [reasons, setReasons] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<TandemPair | null>(null);
  const policy = variant.tandem_eligibility;
  const disabledReason = !policy
    ? 'Reload the blueprint to check tandem availability.'
    : !policy.enabled
      ? 'Tandem comparison is disabled in this environment.'
      : policy.execution_engine !== 'fast'
        ? 'Requires a fast-engine blueprint.'
        : policy.advancement_mode !== 'daily'
          ? 'News advancement cannot run in tandem. Use daily advancement (legacy frequency 1).'
          : !published
            ? 'Publish this version before starting a comparison.'
            : '';
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setReasons(null);
    void Promise.all([
      tandemRequest<Target[]>(
        '/klasses/replay_targets/',
        'GET',
        undefined,
        controller.signal,
      ),
      tandemRequest<{ reasons: string[] }>(
        '/tandem-comparisons/eligibility/',
        'GET',
        { variant_id: variant.id },
        controller.signal,
      ),
    ])
      .then(([rows, eligibility]) => {
        if (!controller.signal.aborted) {
          setTargets(rows.filter(row => row.trainer_id));
          setReasons(eligibility.reasons);
        }
      })
      .catch(reason => {
        if (!controller.signal.aborted) setError(String(reason));
      });
    return () => controller.abort();
  }, [open, variant.id]);
  const submit = async () => {
    if (busy || (!pending && (!trainer || !klass))) return;
    const input = pending || {
      key: crypto.randomUUID(),
      blueprint_id: blueprintId,
      variant_id: variant.id,
      trainer_id: trainer!,
      klass_id: klass!,
    };
    savePending(storage, input);
    setPending(input);
    setBusy(true);
    setError('');
    try {
      const pair = await tandemRequest<TandemPair>(
        '/tandem-comparisons/',
        'POST',
        input,
      );
      setCreated(pair);
      savePending(storage, null);
      setPending(null);
    } catch (reason) {
      setError(String(reason));
      // A definitive client refusal made no pair. Network/5xx outcomes remain
      // uncertain, so the retry retains the original destination and key.
      if (
        reason instanceof TandemError &&
        reason.status >= 400 &&
        reason.status < 500
      ) {
        savePending(storage, null);
        setPending(null);
      }
    } finally {
      setBusy(false);
    }
  };
  const trainers = Array.from(
    new Map(
      targets.map(row => [
        row.trainer_id,
        { value: row.trainer_id, label: row.trainer_username },
      ]),
    ).values(),
  );
  return (
    <>
      <Space size={4} style={{ width: '100%' }}>
        <Tooltip title={disabledReason}>
          <span>
            <Button
              size="small"
              disabled={!!disabledReason && !pending}
              onClick={() => setOpen(true)}
            >
              Start tandem comparison
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Create two sessions from this exact blueprint, one per engine, in the same trainer account and class. Compare them period by period with manual or automatic tandem advances. Place trades separately in each session.">
          <InfoCircleOutlined aria-label="About tandem comparison" />
        </Tooltip>
      </Space>
      {disabledReason && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {disabledReason}
        </Typography.Text>
      )}
      <Modal
        title="Start tandem comparison"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnHidden={false}
      >
        {created ? (
          <Space direction="vertical">
            <Alert
              type="success"
              message="Both sessions created. Initialization is in progress; auto advance is off."
            />
            <Typography.Text>
              {created.sessions
                .map(room => `${room.side}: ${room.id}`)
                .join(' · ')}
            </Typography.Text>
            <Link to={`/dashboard/tandem-comparisons/${created.id}`}>
              <Button type="primary">Open tandem</Button>
            </Link>
            <Button onClick={() => setCreated(null)}>
              Start another comparison
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Paragraph>
              Both sessions use this blueprint's settings. Only the engine
              differs. The class needs at least one active trainee.
            </Typography.Paragraph>
            <Select
              aria-label="Trainer account"
              placeholder="Trainer account"
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              value={trainer}
              options={trainers}
              disabled={busy || !!pending}
              onChange={value => {
                setTrainer(value);
                setKlass(undefined);
              }}
            />
            <Select
              aria-label="Class"
              placeholder="Class"
              style={{ width: '100%' }}
              value={klass}
              options={targets
                .filter(row => row.trainer_id === trainer)
                .map(row => ({ value: row.id, label: row.name }))}
              disabled={!trainer || busy || !!pending}
              onChange={setKlass}
            />
            {reasons?.map(reason => (
              <Alert key={reason} type="warning" message={reason} />
            ))}
            {error && <Alert type="error" message={error} />}
            {pending && !busy && (
              <Alert
                type="info"
                message="The previous response is unconfirmed. Retry checks the same creation request."
              />
            )}
            <Button
              type="primary"
              loading={busy}
              disabled={
                !pending &&
                (!trainer || !klass || reasons === null || reasons.length > 0)
              }
              onClick={() => void submit()}
            >
              {pending ? 'Retry same creation' : 'Create both sessions'}
            </Button>
          </Space>
        )}
      </Modal>
    </>
  );
}
